import React, { Component } from 'react';
import { Tabs, TabList, Tab, TabPanel } from 'react-tabs';
import { RouteComponentProps } from 'react-router';
import b from 'b_';

import { ResearchersRequestParams } from '@interfaces/researchers-request-params';
import { AsyncFetchDataProps } from '@interfaces/async-fetch-data';
import { Researcher } from '@interfaces/researchers-page';
import PaginationMeta from '@interfaces/pagination-meta';
import ResearchersTab from '@interfaces/researchers-tab';
import Theme from '@interfaces/themes';

import { ShowMore, PaginationWrapperProps } from '@components/pagination-wrapper';
import Researchers, { ResearchersStub } from '@components/researchers';
import ThemesSelect from '@components/themes-select';
import PageMeta from '@components/page-meta';
import Button from '@components/button';

import { fetchResearchersPage } from 'client/ducks/researchers/actions';
import { fetchThemes } from 'client/ducks/themes/actions';

import researcherConfig from './page-config.json';

import './researchers-list.css';

export interface DispatchProps {
    fetchPage(params: ResearchersRequestParams): void;
    fetchThemes(): void;
    setTabIndex(index: number): void;
    setThemeSlug(themeSlug?: string | null): void;
}

export interface StateProps {
    meta: PaginationMeta;
    rows: Researcher[];
    themes: Theme[];
    tabs: ResearchersTab[];
    selectedTabIndex: number;
    selectedThemeSlug: string;
    hasMore: boolean;
    isLoading: boolean;
}

type Props = StateProps & DispatchProps & PaginationWrapperProps & RouteComponentProps<any>;

const class_ = b.with('researcher-list');
const allThemesSelectItem = { id: -1, slug: 'all', title: 'All research areas' };

class ResearchersPage extends Component<Props> {
    public static asyncFetchData({ dispatch }: AsyncFetchDataProps) {
        return Promise.resolve().then(() => {
            dispatch(fetchThemes());
            dispatch(fetchResearchersPage());
        });
    }

    public componentDidMount() {
        const { themes, rows, fetchPage } = this.props;

        if (!themes.length) {
            this.props.fetchThemes();
        }

        if (!rows.length) {
            fetchPage();
        }
    }

    public onThemeChange = (themeSlug: string) => {
        const { selectedThemeSlug, setThemeSlug, fetchPage } = this.props;
        const slug = themeSlug === 'all' ? undefined : themeSlug;

        if (selectedThemeSlug !== slug) {
            setThemeSlug(slug);
            fetchPage();
        }
    };

    public onTabSelect = (index: number) => {
        const { setTabIndex, fetchPage } = this.props;

        setTabIndex(index);
        fetchPage();
    };

    public onShowMoreClick = () => {
        const { meta, fetchPage } = this.props;
        const { pageNumber } = meta;

        fetchPage({ page: 1 + pageNumber });
    };

    public renderResearchers() {
        const { isLoading, hasMore, rows } = this.props;

        if (isLoading && !rows.length) {
            return <ResearchersStub itemsCount={5} />;
        }

        if (!rows.length) {
            return (
                <div className={class_('not-found')}>
                    Nothing found that matches your search criteria. Try expanding your search.
                </div>
            );
        }

        return (
            <div>
                <Researchers
                    key="researchers-list"
                    items={rows}
                />
                {hasMore && (
                    <ShowMore
                        key="show-more"
                        onClick={this.onShowMoreClick}
                        isLoading={isLoading}
                    />
                )}
            </div>
        );
    }

    public renderThemesFilter() {
        const { themes, selectedThemeSlug = 'all' } = this.props;
        const filterThemes = [allThemesSelectItem, ...themes];

        return (
            <div className={class_('filter-wrapper')}>
                <ThemesSelect
                    key="themes-select"
                    className={class_('themes-select')}
                    themes={filterThemes as Theme[]}
                    selectedTheme={selectedThemeSlug}
                    onThemeChange={this.onThemeChange}
                />
            </div>
        );
    }

    public renderStatic() {
        const { title, text, meta } = researcherConfig;

        return (
            <div>
                <PageMeta title={meta.title} description={meta.description} />

                <h1 className="block__title">{title}</h1>
                <div className={class_('text')}>{text}</div>
                <div className={class_('link-to-work')}>
                    <Button
                        theme="action"
                        cls="button2_size_xl"
                        to="/jobs"
                    >
                        Join our team
                    </Button>
                </div>
            </div>
        );
    }

    public renderTabs() {
        const { tabs, selectedTabIndex } = this.props;

        return (
            <Tabs
                className={class_('filter-wrapper')}
                selectedIndex={selectedTabIndex}
                onSelect={this.onTabSelect}
            >
                <TabList className="react-tabs__tab-list react-tabs__tab-list_wrap">
                    {tabs.map(({ label }, index) => <Tab key={index}>{label}</Tab>)}
                </TabList>

                {tabs.map((_, index) => <TabPanel key={index} />)}
            </Tabs>
        );
    }

    public render() {
        return (
            <div className={`block ${class_()}`}>
                {this.renderStatic()}
                <div className={class_('filters')}>
                    {this.renderTabs()}
                    {this.renderThemesFilter()}
                </div>
                {this.renderResearchers()}
            </div>
        );
    }
}

export default ResearchersPage;
